import { SettingsV2 } from '@dixa/messenger-api-payloads';
import { boundMethod } from 'autobind-decorator';
import { isAxiosError } from 'axios';
import { MessengerV2, Response } from 'lib-shared-legacy-types';
import { UnionValuesWith } from 'lib-shared-types-utils';

import { ajaxSend, ApiErrorStatusCode, ApiRequest } from 'api/api';

export enum MessengerSortKey {
  CreatedAt = 'created_at',
  Name = 'name',
  UpdatedAt = 'updated_at',
}

export interface MessengersQueryParams {
  offset?: number;
  limit?: number;
}

export class DixaMessengerApiV2 {
  private readonly baseUrl = '/v2/messengers';

  public async fetch(messengerId: string): Promise<MessengerV2> {
    const response = await ajaxSend<Response<MessengerV2>>({
      method: 'GET',
      path: `${this.baseUrl}/${messengerId}`,
    });

    return response.data;
  }

  @boundMethod
  public async fetchAll(options: {
    params?: ApiRequest['params'];
  }): Promise<Response<{ messengers: MessengerV2[] }>> {
    const response = await ajaxSend<Response<{ messengers: MessengerV2[] }>>({
      method: 'GET',
      path: this.baseUrl,
      params: options.params,
    });
    return response;
  }

  public async create(name: string, queue: string): Promise<MessengerV2> {
    const response = await ajaxSend<Response<MessengerV2>>({
      path: this.baseUrl,
      method: 'POST',
      data: { name, queue_id: queue },
    });

    return response.data;
  }

  public async duplicate(messengerId: string): Promise<void> {
    return ajaxSend({
      path: `${this.baseUrl}/${messengerId}/duplicate`,
      method: 'POST',
    });
  }

  async deleteBulletinBoard(messengerId: string): Promise<void> {
    // Check if we have a bulletin board to also delete.
    const messengerSettings = await this.fetch(messengerId);
    const bulletinBoardId =
      messengerSettings.settings?.integrations.bulletinBoard?.boardId;
    if (bulletinBoardId) {
      // NOTE: api is defined in `src/api/bulletinboards but due to legacy / new cross import
      // it has been re-defined here.
      try {
        await ajaxSend({
          path: `/v1/bulletinboards/${bulletinBoardId}`,
          method: 'DELETE',
        });
      } catch (error) {
        // Don't bother the user if the error is not found, they are trying to delete anyway.
        if (isAxiosError(error)) {
          if (error.response?.status === ApiErrorStatusCode.NotFound) {
            return;
          }
          throw error;
        }
      }
    }
  }

  public async delete(messengerId: string): Promise<void> {
    await this.deleteBulletinBoard(messengerId);
    return ajaxSend({
      path: `${this.baseUrl}/${messengerId}`,
      method: 'DELETE',
    });
  }

  public async patch(
    messengerId: string,
    payload: Partial<MessengerV2>,
  ): Promise<MessengerV2> {
    const response = await ajaxSend<Response<MessengerV2>>({
      method: 'PATCH',
      path: `${this.baseUrl}/${messengerId}`,
      data: { updates: payload },
    });

    return response.data;
  }

  /**
   * @param id Messenger id
   * @param settings A partial version of settings. Field that are missing will
   *   be ignored, to explicitly delete a fields from settings, set them to
   *   `null`
   */
  public async patchSettings(
    id: string,
    settings: Partial<UnionValuesWith<SettingsV2.PatchSettings, null>>,
  ): Promise<MessengerV2> {
    const response = await ajaxSend<Response<MessengerV2>>({
      method: 'PATCH',
      path: `${this.baseUrl}/${id}/settings`,
      data: settings,
    });

    return response.data;
  }

  public async uploadLogo(file: File): Promise<{ id: string }> {
    const data = new FormData();
    data.append('file', file);

    const response = await ajaxSend<{ id: string }>({
      method: 'POST',
      path: `${this.baseUrl}/files`,
      headers: {
        contentType: 'multipart/form-data',
      },
      data,
    });

    return response;
  }

  /**
   * @returns A void promise that resolves when the account exists and rejects
   *   if it doesn't.
   */
  public verifyElevioAccount(accountId: string): Promise<void> {
    return ajaxSend({
      method: 'GET',
      path: `${this.baseUrl}/elevio-accounts/${accountId}`,
    });
  }
}
